import { useState, useEffect } from "react";
import { Calendar, MapPin, Users, Target, Flag, Download, HeartHandshake, TrendingUp, Award } from "lucide-react";
import { EVENT, CONTACT } from "@/data/content";

// Print-ready one-page executive summary (8.5 x 11). Route: /exec-summary
const SHEET_W = 816;
const SHEET_H = 1056;

const TIMELINE = [
  { when: "Jul 2026", what: "Committee kickoff · venue & date locked · sponsorship packages finalized" },
  { when: "Aug–Sep 2026", what: "Sponsor outreach begins · website & online registration go live" },
  { when: "Oct–Dec 2026", what: "Secure Presenting & major sponsors · team registration opens" },
  { when: "Jan–Feb 2027", what: "Marketing push · silent auction procurement" },
  { when: "Mar 2027", what: "Final logistics · player communications · volunteer coordination" },
  { when: "Apr 8, 2027", what: "Tournament day — shotgun start, reception & awards" },
  { when: "Apr 2027", what: "Sponsor thank-yous · fund disbursement to the Wayne Walker Foundation · impact recap" },
];

const GOALS = [
  { icon: TrendingUp, label: "Net Fundraising Goal", value: "$125,000", sub: "Directed to the Wayne Walker Foundation" },
  { icon: Users, label: "Player Participation", value: "36 Teams · 144 Players", sub: "Sold-out four-person scramble field" },
  { icon: Award, label: "Sponsorship", value: "6 Levels + Activations", sub: "Legacy · Impact · Courage · Honor · Service · Hero Team" },
];

const OUTCOMES = [
  "Deliver meaningful, direct financial support to our first responders and their families",
  "Publicly honor the men and women who serve, building community awareness of the Foundation's mission",
  "Establish the Heroes & Legacy Golf Classic as a signature annual tradition with returning sponsors",
  "Create a repeatable playbook and sponsor renewal pipeline for future editions",
];

export default function ExecSummary() {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const compute = () => {
      const gutter = window.innerWidth < 640 ? 16 : 48;
      const avail = window.innerWidth - gutter * 2;
      setScale(Math.min(1, avail / SHEET_W));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  return (
    <div className="w-full flex justify-center bg-neutral-800 py-8 px-4 sm:px-0 print:p-0 print:bg-white overflow-hidden">
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          .no-print { display: none !important; }
          .exec-scale-wrap { width: auto !important; height: auto !important; }
          [data-testid="exec-sheet"] { transform: none !important; box-shadow: none !important; break-inside: avoid; page-break-inside: avoid; }
        }
        [data-testid="exec-sheet"], [data-testid="exec-sheet"] * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      `}</style>

      <a
        href="/heroes-legacy-executive-summary.pdf"
        download
        data-testid="exec-print-btn"
        className="no-print fixed top-4 right-4 sm:top-6 sm:right-6 z-50 label-font text-[11px] sm:text-xs bg-rust text-primary-foreground px-4 py-2.5 sm:px-5 sm:py-3 border border-espresso flex items-center gap-2 hard-shadow-sm hover:-translate-y-0.5 transition-transform"
      >
        <Download size={15} /> Download Summary (PDF)
      </a>

      <div className="exec-scale-wrap" style={{ width: SHEET_W * scale, height: SHEET_H * scale }}>
        <div
          data-testid="exec-sheet"
          className="relative bg-lightcream text-espresso paper-noise overflow-hidden"
          style={{ width: `${SHEET_W}px`, height: `${SHEET_H}px`, padding: "40px 52px", transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
          <div className="absolute inset-4 border border-espresso/40 pointer-events-none" />
          <div className="absolute inset-[22px] border-2 border-rust/70 pointer-events-none" />

          <div className="relative z-10 flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-4 border-b-2 border-espresso/20 pb-4">
              <img src="/flat/badge-on-lightcream.jpg" alt="Heroes & Legacy Golf Classic" className="w-20 h-20 object-contain shrink-0" />
              <div className="min-w-0">
                <div className="label-font text-[10px] text-rust tracking-[0.28em]">EXECUTIVE SUMMARY · {EVENT.edition.toUpperCase()}</div>
                <h1 className="uppercase leading-[0.95] mt-1" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: "34px", letterSpacing: "0.01em" }}>
                  The Heroes & Legacy <span className="text-rust">Golf Classic</span>
                </h1>
                <div className="text-[11px] text-espresso/70 mt-0.5" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 500, letterSpacing: "0.04em" }}>
                  Hosted by The Wayne Walker Foundation · Benefiting the {EVENT.beneficiary}
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="mt-4">
              <SectionTitle icon={Flag}>The Vision</SectionTitle>
              <p className="mt-1.5 text-[12.5px] leading-snug text-espresso/85">
                Rooted in service and the Texas outdoors, the Wayne Walker Foundation is launching the Heroes &amp; Legacy Golf Classic
                to unite our community for a single day of camaraderie, competition, and giving. The inaugural Classic channels Wayne
                Walker's legacy of generosity into direct, meaningful support for the first responders who answer the call —
                turning a great day on the course into lasting impact for the men and women who protect our community.
              </p>
            </div>

            {/* Event Details */}
            <div className="mt-4">
              <SectionTitle icon={Calendar}>Event Details</SectionTitle>
              <div className="mt-2 grid grid-cols-4 gap-3">
                <Fact icon={Calendar} label="When" main={EVENT.dateLabel} sub={EVENT.dayLabel} />
                <Fact icon={MapPin} label="Where" main={EVENT.venue} sub="The Trails · 2311 N Millbend Dr, Spring, TX 77380" />
                <Fact icon={Users} label="Format" main="Four-Person" sub="Scramble · Shotgun" />
                <Fact icon={Target} label="Field" main={EVENT.teams} sub="144 players" />
              </div>
              <p className="mt-2 text-[11.5px] leading-snug text-espresso/75">
                The day includes breakfast &amp; on-course food, player gift bags, on-course contests (hole-in-one, longest
                drive, closest-to-pin), a silent auction, and an awards reception honoring our first responders.
              </p>
            </div>

            {/* Two columns: Timeline + Goals */}
            <div className="mt-4 grid grid-cols-2 gap-6 flex-1">
              {/* Timeline */}
              <div>
                <SectionTitle icon={Calendar}>Planning Timeline</SectionTitle>
                <ul className="mt-2 space-y-1.5">
                  {TIMELINE.map((t) => (
                    <li key={t.when} className="flex gap-2.5 text-[11px] leading-snug">
                      <span className="label-font text-[9.5px] text-rust w-[74px] shrink-0 pt-0.5">{t.when}</span>
                      <span className="text-espresso/80">{t.what}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Goals */}
              <div>
                <SectionTitle icon={Target}>Targeted Goals</SectionTitle>
                <div className="mt-2 space-y-2">
                  {GOALS.map((g) => (
                    <div key={g.label} className="border border-espresso/25 bg-cream/60 px-3 py-2">
                      <div className="flex items-center gap-1.5 text-rust">
                        <g.icon size={12} /><span className="label-font text-[9px]">{g.label}</span>
                      </div>
                      <div className="font-serif font-black text-[17px] leading-tight text-espresso">{g.value}</div>
                      <div className="text-[10px] text-espresso/70 leading-tight">{g.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Outcomes */}
            <div className="mt-3">
              <SectionTitle icon={HeartHandshake}>Goals &amp; Outcomes</SectionTitle>
              <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5">
                {OUTCOMES.map((o) => (
                  <div key={o} className="flex items-start gap-2 text-[11px] leading-snug">
                    <span className="mt-1 h-1.5 w-1.5 bg-rust shrink-0" />
                    <span className="text-espresso/85">{o}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-auto pt-3 border-t border-espresso/20 flex items-center justify-between text-[10px] text-espresso/60">
              <span className="label-font tracking-[0.15em]">WAYNE WALKER FOUNDATION · MONTGOMERY COUNTY, TEXAS</span>
              <span>{CONTACT.sponsorEmail} · {CONTACT.sponsorPhone} · {CONTACT.website}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-6 w-6 bg-espresso text-cream flex items-center justify-center shrink-0"><Icon size={13} /></span>
      <h2 className="uppercase text-espresso" style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: "15px", letterSpacing: "0.06em" }}>{children}</h2>
    </div>
  );
}

function Fact({ icon: Icon, label, main, sub }) {
  return (
    <div className="border border-espresso/25 bg-cream/60 py-2.5 px-2 text-center">
      <div className="flex items-center justify-center gap-1 text-rust"><Icon size={11} /><span className="label-font text-[8.5px]">{label}</span></div>
      <div className="font-serif font-bold mt-0.5" style={{ fontSize: "14px" }}>{main}</div>
      <div className="text-[9.5px] text-espresso/70 leading-tight">{sub}</div>
    </div>
  );
}
